import type { Money } from '../api/client'

// Das Vokabular der Handelsideen an einer Stelle.
//
// Die Datenbank spricht englische Konstanten, die Oberfläche deutsch. Beides
// zu vermischen hieße, dass eine Umbenennung an fünf Stellen nachzuziehen
// wäre — und die fünfte vergisst man.

import type { ChipTone } from '../components/common/Chip'

/** Wie mit dem Titel umzugehen ist. */
export const CLASSIFICATION: Record<string, string> = {
  WATCH: 'beobachten',
  STAGED_BUY: 'gestaffelt kaufbar',
  DIP_ONLY: 'nur Rücksetzer',
  TRIGGER_ONLY: 'nur bei Auslöser',
  BOTTOMING_REQUIRED: 'erst nach Bodenbildung',
}

/** Was die Einstufung für eine Entscheidung bedeutet. */
export const CLASSIFICATION_HELP: Record<string, string> = {
  WATCH: 'Im Auge behalten. Noch kein Einstieg.',
  STAGED_BUY: 'In mehreren Tranchen kaufbar, sobald die Auslöser greifen.',
  DIP_ONLY: 'Nur bei einem Rücksetzer in die genannte Zone.',
  TRIGGER_ONLY: 'Erst, wenn der genannte Auslöser eintritt — nicht vorher.',
  BOTTOMING_REQUIRED: 'Erst nach bestätigter Bodenbildung. Der Abverkauf läuft noch.',
}

/** Wo die Idee steht. */
export const STATUS: Record<string, string> = {
  PROPOSED: 'vorgeschlagen',
  // „angenommen" und nicht „gilt": So spricht man über einen Vorschlag —
  // man nimmt ihn an oder lehnt ihn ab.
  ACTIVE: 'angenommen',
  ENTERED: 'im Markt',
  INVALIDATED: 'ungültig',
  EXPIRED: 'abgelaufen',
  CLOSED: 'abgeschlossen',
  REJECTED: 'abgelehnt',
}

export const STATUS_HELP: Record<string, string> = {
  PROPOSED: 'Vom Agenten vorgeschlagen, noch nicht angenommen.',
  ACTIVE: 'Angenommen und wartet auf den Einstieg.',
  ENTERED: 'Stücke im Depot. Ergibt sich aus den Transaktionen und wird nie von Hand gesetzt — solange Stücke da sind, lässt sich der Status nicht ändern.',
  INVALIDATED: 'Die Ungültigkeitsschwelle wurde unterschritten — die Idee ist widerlegt.',
  EXPIRED: 'Die Frist lief ab oder der Kurs ist davongelaufen.',
  CLOSED: 'Abgeschlossen. Nur möglich, wenn keine Stücke mehr da sind.',
  REJECTED: 'Vom Menschen abgelehnt, mit Begründung — eine Entscheidung über das eigene Geld, kein Urteil über die Idee.',
}

/** Womit sich eine Idee abschließen lässt — und was das jeweils heißt. */
export const ENDE: Record<string, string> = {
  CLOSED: 'abgeschlossen — die Idee ist erledigt',
  INVALIDATED: 'ungültig — die Schwelle wurde unterschritten',
  EXPIRED: 'abgelaufen — Frist vorbei oder Kurs davongelaufen',
}

/** Die Seiten einer Order oder Transaktion. */
export const SIDE: Record<string, string> = {
  BUY: 'Kauf',
  SELL: 'Verkauf',
}

// Bis zu vier Nachkommastellen: Bruchstücke sind erlaubt, und „100" soll
// nicht als „100,00" dastehen — eine Stückzahl ist kein Betrag.
const MENGE = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 4 })

/** Eine Stückzahl, deutsch. Die Zeichenkette vom Server bleibt, wenn sie keine Zahl ist. */
export function stueckzahl(wert: string): string {
  const n = Number(wert)
  return Number.isFinite(n) ? MENGE.format(n) : wert
}

export function statusTone(status: string): ChipTone {
  if (status === 'ACTIVE' || status === 'ENTERED') return 'good'
  if (status === 'PROPOSED') return 'info'
  if (status === 'INVALIDATED') return 'bad'
  if (status === 'EXPIRED') return 'warn'
  // Abgelehnt ist keine Warnung und kein Fehler — eine Entscheidung, grau.
  return 'neutral'
}

export const RISK_PROFILE: Record<string, string> = {
  BALANCED: 'ausgewogen',
  AGGRESSIVE: 'aggressiv',
}

/** Die Auslöser, als Satz. */
export const TRIGGER: Record<string, string> = {
  LIMIT: 'Kurs in der Spanne',
  BREAKOUT: 'Ausbruch über',
  CLOSE_ABOVE: 'Schlusskurs über',
  RETEST: 'Rücktest von',
  RECLAIM: 'Rückeroberung von',
}

export const TRIGGER_HELP: Record<string, string> = {
  LIMIT: 'Der Kurs liegt in der genannten Spanne.',
  BREAKOUT: 'Der Kurs steigt über die Marke — bei einer Bestätigungsdauer muss er dort auch bleiben.',
  CLOSE_ABOVE: 'Der SCHLUSSKURS liegt darüber. Ein Zwischenhoch genügt nicht.',
  RETEST: 'Nach einem Ausbruch fällt der Kurs auf die Marke zurück und hält sie.',
  RECLAIM: 'Ein verlorenes Niveau wird zurückerobert.',
}

/** Ein Auslöser als lesbarer Satz, mit eingesetzten Werten. */
export function triggerText(t: {
  trigger_type: string
  price: Money
  price_high: Money | null
  confirm_minutes: number | null
  max_spread: Money | null
}, geldtext: (v: Money) => string): string {
  const teile: string[] = [TRIGGER[t.trigger_type] ?? t.trigger_type]
  // DIE WÄHRUNG STECKT IM BETRAG und wird nicht mehr daneben gereicht: Beide
  // Kanten einer Spanne stehen ohnehin in derselben — eine Untergrenze in
  // Dollar und eine Obergrenze in Euro wäre kein Auslöser, sondern ein
  // Fehler.
  teile.push(t.price_high
    ? `${geldtext(t.price)}–${geldtext(t.price_high)}`
    : geldtext(t.price))
  if (t.confirm_minutes) teile.push(`· ${t.confirm_minutes} Min. bestätigt`)
  if (t.max_spread) teile.push(`· Spread höchstens ${geldtext(t.max_spread)}`)
  return teile.join(' ')
}
